import { useState } from 'react';
import {
  Title,
  Text,
  Stack,
  Paper,
  Table,
  Button,
  Group,
  ActionIcon,
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  Badge,
  Skeleton,
  Alert,
  Drawer,
  Checkbox,
  Divider,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconDoor,
  IconAlertTriangle,
  IconDeviceDesktop,
  IconX,
  IconCheck,
  IconBolt,
} from '@tabler/icons-react';

import {
  useRooms,
  useRoomTypes,
  useCreateRoom,
  useUpdateRoom,
  useDeleteRoom,
  useRoomDevices,
  useUpdateDeviceRoom,
  useSites,
  useDevices,
  useWakeOnLanRoom,
} from '@/hooks';
import { Room } from '@/types';

const ROOM_TYPE_COLORS: Record<string, string> = {
  classroom: 'blue',
  lab: 'grape',
  office: 'teal',
  server_room: 'orange',
  other: 'gray',
};

export function RoomsPage() {
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const { data: rooms, isLoading, isError, refetch: refetchRooms } = useRooms(selectedSiteId || undefined);
  const { data: roomTypes } = useRoomTypes();
  const { data: sites } = useSites();
  const createRoom = useCreateRoom();
  const updateRoom = useUpdateRoom();
  const deleteRoom = useDeleteRoom();
  const updateDeviceRoom = useUpdateDeviceRoom();
  const wakeOnLanRoom = useWakeOnLanRoom();

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure();
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure();
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure();
  const [addDevicesModalOpened, { open: openAddDevicesModal, close: closeAddDevicesModal }] = useDisclosure();

  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [deletingRoom, setDeletingRoom] = useState<Room | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [selectedDevicesToAdd, setSelectedDevicesToAdd] = useState<string[]>([]);

  // Fetch devices for the selected room
  const { data: roomDevicesData, refetch: refetchRoomDevices } = useRoomDevices(selectedRoom?.id || '');

  // Fetch all devices without a room for adding (filter by site)
  const { data: devicesData } = useDevices({
    page: 1,
    limit: 100,
    site: selectedRoom?.siteId || '',
  });

  // Filter devices that are not assigned to any room (or at least not this room)
  const availableDevices = devicesData?.devices.filter(d => !(d as any).roomId) || [];

  // Form state
  const [formData, setFormData] = useState({
    siteId: '',
    roomCode: '',
    roomName: '',
    roomType: 'other' as string,
    floor: '',
    capacity: null as number | null,
    description: '',
  });

  const resetForm = () => {
    setFormData({
      siteId: selectedSiteId || '',
      roomCode: '',
      roomName: '',
      roomType: 'other',
      floor: '',
      capacity: null,
      description: '',
    });
    setEditingRoom(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    openModal();
  };

  const handleOpenEdit = (room: Room, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingRoom(room);
    setFormData({
      siteId: room.siteId,
      roomCode: room.roomCode,
      roomName: room.roomName,
      roomType: room.roomType,
      floor: room.floor || '',
      capacity: room.capacity,
      description: room.description || '',
    });
    openModal();
  };

  const handleOpenDelete = (room: Room, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingRoom(room);
    openDeleteModal();
  };

  const handleRowClick = (room: Room) => {
    setSelectedRoom(room);
    openDrawer();
  };

  const handleSubmit = async () => {
    if (!formData.siteId) {
      notifications.show({
        title: 'Error',
        message: 'Please select a site',
        color: 'red',
      });
      return;
    }

    try {
      if (editingRoom) {
        await updateRoom.mutateAsync({
          id: editingRoom.id,
          data: {
            roomCode: formData.roomCode,
            roomName: formData.roomName,
            roomType: formData.roomType,
            floor: formData.floor || null,
            capacity: formData.capacity,
            description: formData.description || null,
          },
        });
        notifications.show({
          title: 'Room updated',
          message: `${formData.roomName} has been updated`,
          color: 'green',
        });
      } else {
        await createRoom.mutateAsync({
          siteId: formData.siteId,
          roomCode: formData.roomCode,
          roomName: formData.roomName,
          roomType: formData.roomType,
          floor: formData.floor || undefined,
          capacity: formData.capacity || undefined,
          description: formData.description || undefined,
        });
        notifications.show({
          title: 'Room created',
          message: `${formData.roomName} has been created`,
          color: 'green',
        });
      }
      closeModal();
      resetForm();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save room',
        color: 'red',
      });
    }
  };

  const handleDelete = async () => {
    if (!deletingRoom) return;

    try {
      await deleteRoom.mutateAsync(deletingRoom.id);
      notifications.show({
        title: 'Room deleted',
        message: `${deletingRoom.roomName} has been deleted`,
        color: 'green',
      });
      closeDeleteModal();
      setDeletingRoom(null);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete room',
        color: 'red',
      });
    }
  };

  const handleRemoveDeviceFromRoom = async (deviceId: string) => {
    try {
      await updateDeviceRoom.mutateAsync({ deviceId, roomId: null });
      notifications.show({
        title: 'Device removed',
        message: 'Device has been removed from this room',
        color: 'green',
      });
      refetchRoomDevices();
      refetchRooms();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to remove device',
        color: 'red',
      });
    }
  };

  const handleAddDevicesToRoom = async () => {
    if (!selectedRoom || selectedDevicesToAdd.length === 0) return;

    try {
      // Add devices one by one
      for (const deviceId of selectedDevicesToAdd) {
        await updateDeviceRoom.mutateAsync({ deviceId, roomId: selectedRoom.id });
      }
      notifications.show({
        title: 'Devices added',
        message: `${selectedDevicesToAdd.length} device(s) added to ${selectedRoom.roomName}`,
        color: 'green',
      });
      setSelectedDevicesToAdd([]);
      closeAddDevicesModal();
      refetchRoomDevices();
      refetchRooms();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to add devices',
        color: 'red',
      });
    }
  };

  const handleSelectDeviceToAdd = (deviceId: string, checked: boolean) => {
    if (checked) {
      setSelectedDevicesToAdd(prev => [...prev, deviceId]);
    } else {
      setSelectedDevicesToAdd(prev => prev.filter(id => id !== deviceId));
    }
  };

  const siteOptions = sites?.map((site) => ({
    value: site.id,
    label: `${site.code} - ${site.name}`,
  })) || [];

  const roomTypeOptions = roomTypes?.map((type) => ({
    value: type.value,
    label: type.label,
  })) || [
    { value: 'classroom', label: 'Classroom' },
    { value: 'lab', label: 'Computer Lab' },
    { value: 'office', label: 'Office' },
    { value: 'server_room', label: 'Server Room' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <>
      <Stack gap="lg">
        {/* Page header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Rooms</Title>
            <Text c="dimmed" size="sm">
              Manage rooms within sites
            </Text>
          </div>
          <Group>
            <Select
              placeholder="Filter by site"
              data={[{ value: '', label: 'All sites' }, ...siteOptions]}
              value={selectedSiteId || ''}
              onChange={(value) => setSelectedSiteId(value || null)}
              clearable
              w={250}
            />
            <Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreate}>
              Add Room
            </Button>
          </Group>
        </Group>

        {/* Rooms table */}
        <Paper withBorder>
          {isLoading ? (
            <Stack p="md" gap="sm">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} height={50} />
              ))}
            </Stack>
          ) : isError ? (
            <Alert color="red" m="md">
              Failed to load rooms
            </Alert>
          ) : rooms?.length === 0 ? (
            <Stack align="center" p="xl" gap="md">
              <IconDoor size={48} color="gray" />
              <Text c="dimmed">No rooms configured</Text>
              <Button variant="light" onClick={handleOpenCreate}>
                Create your first room
              </Button>
            </Stack>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Site</Table.Th>
                  <Table.Th>Code</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Floor</Table.Th>
                  <Table.Th>Capacity</Table.Th>
                  <Table.Th>Devices</Table.Th>
                  <Table.Th w={100}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rooms?.map((room) => (
                  <Table.Tr
                    key={room.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(room)}
                  >
                    <Table.Td>
                      <Text size="sm">{room.siteName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500} ff="monospace" size="sm">
                        {room.roomCode}
                      </Text>
                    </Table.Td>
                    <Table.Td>{room.roomName}</Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        size="sm"
                        color={ROOM_TYPE_COLORS[room.roomType] || 'gray'}
                      >
                        {room.roomTypeLabel}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {room.floor ? (
                        <Text size="sm">{room.floor}</Text>
                      ) : (
                        <Text c="dimmed" size="sm">-</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {room.capacity ? (
                        <Text size="sm">{room.capacity}</Text>
                      ) : (
                        <Text c="dimmed" size="sm">-</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <IconDeviceDesktop size={14} />
                        <Text size="sm">{room.deviceCount}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Group gap="xs">
                        <Tooltip label="Wake all devices">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            color="yellow"
                            onClick={(e) => {
                              e.stopPropagation();
                              wakeOnLanRoom.mutate({ roomId: room.id });
                            }}
                            loading={wakeOnLanRoom.isPending}
                            disabled={room.deviceCount === 0}
                          >
                            <IconBolt size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={(e) => handleOpenEdit(room, e)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="red"
                          onClick={(e) => handleOpenDelete(room, e)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </Stack>

      {/* Room Devices Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={
          <Group>
            <IconDoor size={20} />
            <Text fw={600}>{selectedRoom?.roomName}</Text>
          </Group>
        }
        size="lg"
        position="right"
      >
        {selectedRoom && (
          <Stack gap="md">
            {/* Room Info */}
            <Paper p="md" withBorder>
              <Group justify="space-between">
                <div>
                  <Text size="sm" c="dimmed">Site</Text>
                  <Text>{selectedRoom.siteName}</Text>
                </div>
                <div>
                  <Text size="sm" c="dimmed">Type</Text>
                  <Badge color={ROOM_TYPE_COLORS[selectedRoom.roomType] || 'gray'}>
                    {selectedRoom.roomTypeLabel}
                  </Badge>
                </div>
                <div>
                  <Text size="sm" c="dimmed">Floor</Text>
                  <Text>{selectedRoom.floor || '-'}</Text>
                </div>
                <div>
                  <Text size="sm" c="dimmed">Capacity</Text>
                  <Text>{selectedRoom.capacity || '-'}</Text>
                </div>
              </Group>
            </Paper>

            <Divider />

            {/* Devices Header */}
            <Group justify="space-between">
              <Text fw={500}>
                Devices ({roomDevicesData?.total || 0})
              </Text>
              <Button
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => {
                  setSelectedDevicesToAdd([]);
                  openAddDevicesModal();
                }}
              >
                Add Devices
              </Button>
            </Group>

            {/* Devices List */}
            {roomDevicesData?.devices && roomDevicesData.devices.length > 0 ? (
              <Stack gap="xs">
                {roomDevicesData.devices.map((device: any) => (
                  <Paper key={device.id} p="sm" withBorder>
                    <Group justify="space-between">
                      <Group gap="sm">
                        <IconDeviceDesktop size={16} />
                        <div>
                          <Text size="sm" fw={500}>{device.hostname}</Text>
                          <Text size="xs" c="dimmed">
                            {device.ipAddress || 'No IP'} | {device.deviceType}
                          </Text>
                        </div>
                      </Group>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => handleRemoveDeviceFromRoom(device.id)}
                        loading={updateDeviceRoom.isPending}
                        title="Remove from room"
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Paper p="xl" withBorder>
                <Stack align="center" gap="sm">
                  <IconDeviceDesktop size={32} color="gray" />
                  <Text c="dimmed" size="sm">No devices in this room</Text>
                  <Button
                    variant="light"
                    size="xs"
                    onClick={() => {
                      setSelectedDevicesToAdd([]);
                      openAddDevicesModal();
                    }}
                  >
                    Add devices
                  </Button>
                </Stack>
              </Paper>
            )}
          </Stack>
        )}
      </Drawer>

      {/* Add Devices Modal */}
      <Modal
        opened={addDevicesModalOpened}
        onClose={() => {
          closeAddDevicesModal();
          setSelectedDevicesToAdd([]);
        }}
        title={
          <Group gap="xs">
            <IconPlus size={20} />
            <Text fw={600}>Add Devices to {selectedRoom?.roomName}</Text>
          </Group>
        }
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Select devices from {selectedRoom?.siteName} to add to this room.
          </Text>

          {availableDevices.length > 0 ? (
            <>
              <Paper withBorder style={{ maxHeight: 400, overflowY: 'auto' }}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={40}></Table.Th>
                      <Table.Th>Hostname</Table.Th>
                      <Table.Th>IP</Table.Th>
                      <Table.Th>Type</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {availableDevices.map((device) => (
                      <Table.Tr key={device.id}>
                        <Table.Td>
                          <Checkbox
                            checked={selectedDevicesToAdd.includes(device.id)}
                            onChange={(e) => handleSelectDeviceToAdd(device.id, e.currentTarget.checked)}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>{device.hostname}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" ff="monospace">{device.ipAddress || '-'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="xs" variant="light">{device.deviceType}</Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>

              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {selectedDevicesToAdd.length} device(s) selected
                </Text>
                <Group gap="sm">
                  <Button variant="subtle" onClick={() => { closeAddDevicesModal(); setSelectedDevicesToAdd([]); }}>
                    Cancel
                  </Button>
                  <Button
                    leftSection={<IconCheck size={16} />}
                    onClick={handleAddDevicesToRoom}
                    loading={updateDeviceRoom.isPending}
                    disabled={selectedDevicesToAdd.length === 0}
                  >
                    Add to Room
                  </Button>
                </Group>
              </Group>
            </>
          ) : (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
              No unassigned devices available at this site. Devices already assigned to rooms are not shown.
            </Alert>
          )}
        </Stack>
      </Modal>

      {/* Create/Edit Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => {
          closeModal();
          resetForm();
        }}
        title={editingRoom ? 'Edit Room' : 'Create Room'}
        size="md"
      >
        <Stack gap="md">
          <Select
            label="Site"
            placeholder="Select a site"
            description="The site this room belongs to"
            required
            data={siteOptions}
            value={formData.siteId}
            onChange={(value) => setFormData({ ...formData, siteId: value || '' })}
            disabled={!!editingRoom}
          />
          <TextInput
            label="Room Code"
            placeholder="LAB-101"
            description="Short unique identifier within the site"
            required
            value={formData.roomCode}
            onChange={(e) => setFormData({ ...formData, roomCode: e.target.value })}
          />
          <TextInput
            label="Room Name"
            placeholder="Computer Lab 101"
            description="Display name for the room"
            required
            value={formData.roomName}
            onChange={(e) => setFormData({ ...formData, roomName: e.target.value })}
          />
          <Select
            label="Room Type"
            placeholder="Select type"
            description="Category of the room"
            data={roomTypeOptions}
            value={formData.roomType}
            onChange={(value) => setFormData({ ...formData, roomType: value || 'other' })}
          />
          <TextInput
            label="Floor"
            placeholder="1st Floor"
            description="Floor location (optional)"
            value={formData.floor}
            onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
          />
          <NumberInput
            label="Capacity"
            placeholder="30"
            description="Number of seats/workstations (optional)"
            min={0}
            value={formData.capacity || ''}
            onChange={(value) => setFormData({ ...formData, capacity: typeof value === 'number' ? value : null })}
          />
          <Textarea
            label="Description"
            placeholder="Additional notes about this room..."
            description="Optional description"
            minRows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => { closeModal(); resetForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={createRoom.isPending || updateRoom.isPending}
            >
              {editingRoom ? 'Save Changes' : 'Create Room'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => {
          closeDeleteModal();
          setDeletingRoom(null);
        }}
        title="Delete Room"
        size="sm"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
            Are you sure you want to delete <strong>{deletingRoom?.roomName}</strong>?
            {deletingRoom && deletingRoom.deviceCount > 0 && (
              <Text size="sm" mt="xs">
                This room has {deletingRoom.deviceCount} device(s) assigned. They will be unassigned.
              </Text>
            )}
          </Alert>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => { closeDeleteModal(); setDeletingRoom(null); }}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteRoom.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
